import {Injectable, NgModule} from "@angular/core";

import {AccountGovernanceUser} from "app/account-governance/account-governance-user";
import {AiAgentsService} from "app/services/domain/ai-agents.service";
import {AIUnitEconomicsService} from "app/services/domain/ai-unit-economics.service";
import {AnomalyService} from "app/services/domain/anomaly.service";
import {ApiService} from "app/services/domain/api.service";
import {APP_DOMAIN_CONTEXT_TOKEN} from "app/services/domain/app-domain-context.token";
import {AppOnboardingApplicationService} from "app/services/domain/app-onboarding-application.service";
import {AppOnboardingPortfolioService} from "app/services/domain/app-onboarding-portfolio.service";
import {AppOnboardingProjectService} from "app/services/domain/app-onboarding-project.service";
import {AppInsightsService} from "app/services/domain/appsecops-appinsights.service";
import {AttachmentsService} from "app/services/domain/attachments.service";
import {AuditLogService} from "app/services/domain/audit-log.service";
import {AuthGuard} from "app/services/domain/auth.guard";
import {AutomationTemplatesService} from "app/services/domain/automation-templates.service";
import {AwsSecurityHubService} from "app/services/domain/aws-security-hub.service";
import {AwsSsmDocumentService} from "app/services/domain/aws-ssm-document.service";
import {AwsSsmExecutionService} from "app/services/domain/aws-ssm-execution.service";
import {BillingMetricsListService} from "app/services/domain/billing/billing-metrics-list.service";
import {BillingPlanDefinitionListService} from "app/services/domain/billing/billing-plan/billing-plan-definition-list.service";
import {BillingPlanDefinitionsService} from "app/services/domain/billing/billing-plan/billing-plan-definitions.service";
import {BillingPlanDefinitionVersionsService} from "app/services/domain/billing/billing-plan/billing-plan-versions.service";
import {BillingService} from "app/services/domain/billing/billing.service";
import {ClipboardService} from "app/services/domain/clipboard.service";
import {CmdbSyncLogsService} from "app/services/domain/cmdb-sync-logs.service";
import {ComplianceAssessmentControlListService} from "app/services/domain/compliance-assessment-control-list.service";
import {ComplianceAssessmentFiltersService} from "app/services/domain/compliance-assessment-filters.service";
import {ComplianceAssessmentHistoryListService} from "app/services/domain/compliance-assessment-history-list.service";
import {ComplianceControlsListService} from "app/services/domain/compliance-controls-list.service";
import {ComplianceStandardService} from "app/services/domain/compliance-standard.service";
import {ComplianceStandardsListService} from "app/services/domain/compliance-standards-list.service";
import {CurrencyExchangePlanService} from "app/services/domain/currency-exchange.service";
import {DashboardDefinitionVersionsService} from "app/services/domain/dashboard-definition-versions.service";
import {DashboardDefinitionsService} from "app/services/domain/dashboard-definitions.service";
import {DashboardManagementService} from "app/services/domain/dashboard-management.service";
import {DeepLinkService} from "app/services/domain/deep-link.service";
import {DimensionDefinitionListService} from "app/services/domain/dimensions/dimension-definition-list.service";
import {DimensionDefinitionVersionService} from "app/services/domain/dimensions/dimension-definition-version.service";
import {DimensionDefinitionService} from "app/services/domain/dimensions/dimension-definition.service";
import {DimensionUpdatedService} from "app/services/domain/dimensions/dimension-updated";
import {DimensionsService} from "app/services/domain/dimensions/dimensions.service";
import {PlatformTypeService} from "app/services/domain/platform-type.service";
import {FilterOptionsService} from "app/services/domain/filter-options/filter-options.service";
import {PolicyExclusionService} from "app/services/domain/policy-exclusion.service";
import {PolicyListService} from "app/services/domain/policy-list.service";
import {PolicyParameterConfigService} from "app/services/domain/policy-parameter-config.service";
import {ProductsService} from "app/services/domain/products.service";
import {ReportGenerateDownloadService} from "app/services/domain/report-generate-download.service";
import {SbomService} from "app/services/domain/sbom.service";
import {ScheduleService} from "app/services/domain/schedule.service";
import {AccountGovernanceListService} from "app/services/domain/service-accounts/account-governance-list.service";
import {ServiceAccountSummaryStore} from "app/services/domain/service-accounts/cloud-account-cache.service";
import {ServiceAccountService} from "app/services/domain/service-accounts/service-account.service";
import {SyncStatusService} from "app/services/domain/sync-status.service";
import {TagBaselineAvailableTagsListService} from "app/services/domain/tag-baseline-available-tags.service";
import {TagBaselineDefinitionsService} from "app/services/domain/tag-baseline-definitions.service";
import {TagBaselineDefinitionVersionsService} from "app/services/domain/tag-baseline-versions.service";
import {TagPostureCloudAccountSummaryService} from "app/services/domain/tag-posture-cloud-account-summary.service";
import {TagPostureResourceSummaryService} from "app/services/domain/tag-posture-resource-summary.service";
import {TagPostureSnapshotService} from "app/services/domain/tag-posture-snapshot.service";
import {TagPostureService} from "app/services/domain/tag-posture.service";
import {TemplateJobsListService} from "app/services/domain/template-jobs-list.service";
import {TemplatesListService} from "app/services/domain/templates-list.service";
import {ThemeService} from "app/services/domain/theme.service";
import {TransactionLogService} from "app/services/domain/transaction-log.service";
import {UserPreferenceService} from "app/services/domain/user-preference.service";
import {UsersService} from "app/services/domain/users.service";
import {VmAgentsService} from "app/services/domain/vm-agents.service";
import {WafAssessmentDefinitionsService, WafAssessmentsListService} from "app/services/domain/waf-assessment-definitions.service";
import {WafAssessmentRunService} from "app/services/domain/waf-assessment-run.service";
import {WafAssessmentVersionsService} from "app/services/domain/waf-assessment-versions.service";
import {WafFrameworksService} from "app/services/domain/waf-frameworks.service";
import {WidgetDataService} from "app/services/domain/widget-data.service";
import {WorkloadDefinitionsService} from "app/services/domain/workload-definitions.service";
import {WorkloadQueryService} from "app/services/domain/workload-query.service";
import {WorkloadDefinitionVersionsService} from "app/services/domain/workload-versions.service";
import {WorkloadsListService} from "app/services/domain/workloads.service";
import {FeatureFlagsRequester} from "app/services/third-party/feature-flags/feature-flags-requester.service";
import {FEATURE_FLAGS_PROVIDER, FeatureFlagsService, IFeatureFlagsProvider} from "app/services/third-party/feature-flags/feature-flags.service";
import {InjectedFeatureFlagsProvider} from "app/services/third-party/feature-flags/injected-feature-flags.service";
import {LaunchDarklyFeatureFlagsProvider} from "app/services/third-party/feature-flags/launch-darkly-feature-flags.service";

import {ApiClientConfiguration} from "framework/services/api.client";
import {SwaggerService} from "framework/services/swagger.service";
import {FullscreenViewService} from "framework/ui/fullscreen-view/fullscreen-view.service";

@Injectable({providedIn: "root"})
export class AppDomainContext
{
    constructor(public accountGovernanceList: AccountGovernanceListService,
                public aiAgents: AiAgentsService,
                public aiUnitEconomics: AIUnitEconomicsService,
                public anomaly: AnomalyService,
                public apiConfiguration: ApiClientConfiguration,
                public apis: ApiService,
                public appOnboardingApplication: AppOnboardingApplicationService,
                public appOnboardingPortfolio: AppOnboardingPortfolioService,
                public appOnboardingProject: AppOnboardingProjectService,
                public appSecOpsAppInsights: AppInsightsService,
                public attachments: AttachmentsService,
                public auditLog: AuditLogService,
                public automationTemplates: AutomationTemplatesService,
                public awsSecurityHub: AwsSecurityHubService,
                public awsSsmDocument: AwsSsmDocumentService,
                public awsSsmExecution: AwsSsmExecutionService,
                public billing: BillingService,
                public billingMetricsList: BillingMetricsListService,
                public billingPlanDefinitions: BillingPlanDefinitionsService,
                public billingPlanDefinitionVersions: BillingPlanDefinitionVersionsService,
                public billingPlansService: BillingPlanDefinitionListService,
                public cmdbSyncLogs: CmdbSyncLogsService,
                public complianceAssessmentControlList: ComplianceAssessmentControlListService,
                public complianceAssessmentFilters: ComplianceAssessmentFiltersService,
                public complianceAssessmentHistoryList: ComplianceAssessmentHistoryListService,
                public complianceControlsList: ComplianceControlsListService,
                public complianceStandardService: ComplianceStandardService,
                public complianceStandardsList: ComplianceStandardsListService,
                public currencyExchange: CurrencyExchangePlanService,
                public dashboard: DashboardManagementService,
                public dashboardDefinitions: DashboardDefinitionsService,
                public dashboardDefinitionVersions: DashboardDefinitionVersionsService,
                public deepLink: DeepLinkService,
                public dimensionDefinition: DimensionDefinitionService,
                public dimensionDefinitionList: DimensionDefinitionListService,
                public dimensionDefinitionVersion: DimensionDefinitionVersionService,
                public dimensions: DimensionsService,
                public featureFlags: FeatureFlagsService,
                public featureFlagsRequester: FeatureFlagsRequester,
                public filterOptions: FilterOptionsService,
                public fullPageView: FullscreenViewService,
                public platformType: PlatformTypeService,
                public policyExclusionService: PolicyExclusionService,
                public policyListService: PolicyListService,
                public policyParamConfig: PolicyParameterConfigService,
                public productsService: ProductsService,
                public reportGenerateDownload: ReportGenerateDownloadService,
                public sbomService: SbomService,
                public scheduleService: ScheduleService,
                public serviceAccountService: ServiceAccountService,
                public summaryStore: ServiceAccountSummaryStore,
                public swagger: SwaggerService,
                public syncStatusService: SyncStatusService,
                public tagBaselineAvailableTags: TagBaselineAvailableTagsListService,
                public tagBaselineDefinitions: TagBaselineDefinitionsService,
                public tagBaselineDefinitionVersions: TagBaselineDefinitionVersionsService,
                public tagPostureCloudAccountSummary: TagPostureCloudAccountSummaryService,
                public tagPostureResourceSummary: TagPostureResourceSummaryService,
                public tagPostureSnapshot: TagPostureSnapshotService,
                public templateJobsList: TemplateJobsListService,
                public templatesList: TemplatesListService,
                public transactionLog: TransactionLogService,
                public users: UsersService,
                public vmAgentService: VmAgentsService,
                public wafAssessmentDefinitions: WafAssessmentDefinitionsService,
                public wafAssessmentList: WafAssessmentsListService,
                public wafAssessmentRuns: WafAssessmentRunService,
                public wafAssessmentVersions: WafAssessmentVersionsService,
                public wafFrameworks: WafFrameworksService,
                public widgetData: WidgetDataService,
                public workloadDefinitions: WorkloadDefinitionsService,
                public workloadDefinitionVersions: WorkloadDefinitionVersionsService,
                public workloadQuery: WorkloadQueryService,
                public workloadsList: WorkloadsListService)
    {
    }

    public getAccountGovernanceUser(): AccountGovernanceUser
    {
        return new AccountGovernanceUser(this.users.userDetails, this.users.activeTenantId);
    }
}

@NgModule({
              providers: [
                  {
                      provide    : APP_DOMAIN_CONTEXT_TOKEN,
                      useExisting: AppDomainContext
                  },
                  AccountGovernanceListService,
                  AiAgentsService,
                  AIUnitEconomicsService,
                  AnomalyService,
                  ApiService,
                  AppInsightsService,
                  AppOnboardingApplicationService,
                  AppOnboardingPortfolioService,
                  AppOnboardingProjectService,
                  AttachmentsService,
                  AuditLogService,
                  AuthGuard,
                  AutomationTemplatesService,
                  AwsSecurityHubService,
                  AwsSsmDocumentService,
                  AwsSsmExecutionService,
                  BillingMetricsListService,
                  BillingPlanDefinitionListService,
                  BillingPlanDefinitionsService,
                  BillingPlanDefinitionVersionsService,
                  BillingService,
                  ClipboardService,
                  CmdbSyncLogsService,
                  ComplianceAssessmentControlListService,
                  ComplianceAssessmentFiltersService,
                  ComplianceAssessmentHistoryListService,
                  ComplianceControlsListService,
                  ComplianceStandardService,
                  ComplianceStandardsListService,
                  CurrencyExchangePlanService,
                  DashboardDefinitionsService,
                  DashboardDefinitionVersionsService,
                  DashboardManagementService,
                  DeepLinkService,
                  DimensionDefinitionListService,
                  DimensionDefinitionService,
                  DimensionDefinitionVersionService,
                  DimensionsService,
                  DimensionUpdatedService,
                  FeatureFlagsRequester,
                  FeatureFlagsService,
                  InjectedFeatureFlagsProvider,
                  LaunchDarklyFeatureFlagsProvider,
                  {
                      provide   : FEATURE_FLAGS_PROVIDER,
                      useFactory: (injected: InjectedFeatureFlagsProvider,
                                   launchDarkly: LaunchDarklyFeatureFlagsProvider): IFeatureFlagsProvider =>
                          // Report pages can't reach LaunchDarkly (network-isolated), so use the server-injected flag snapshot instead.
                          window.CS_REPORT_CONFIG ? injected : launchDarkly,
                      deps      : [
                          InjectedFeatureFlagsProvider,
                          LaunchDarklyFeatureFlagsProvider
                      ]
                  },
                  PlatformTypeService,
                  FilterOptionsService,
                  PolicyExclusionService,
                  PolicyListService,
                  PolicyParameterConfigService,
                  ProductsService,
                  ReportGenerateDownloadService,
                  SbomService,
                  ScheduleService,
                  ServiceAccountService,
                  SwaggerService,
                  SyncStatusService,
                  TagBaselineAvailableTagsListService,
                  TagBaselineDefinitionsService,
                  TagBaselineDefinitionVersionsService,
                  TagPostureCloudAccountSummaryService,
                  TagPostureResourceSummaryService,
                  TagPostureService,
                  TagPostureSnapshotService,
                  TemplateJobsListService,
                  TemplatesListService,
                  ThemeService,
                  TransactionLogService,
                  UserPreferenceService,
                  UsersService,
                  VmAgentsService,
                  WafAssessmentDefinitionsService,
                  WafAssessmentRunService,
                  WafAssessmentsListService,
                  WafAssessmentVersionsService,
                  WafFrameworksService,
                  WidgetDataService,
                  WorkloadDefinitionsService,
                  WorkloadDefinitionVersionsService,
                  WorkloadQueryService,
                  WorkloadsListService
              ]
          })
export class DomainModule {}
